if (!customElements.get('cart-drawer-upsell')) {
  const recCache = new Map();

  class CartDrawerUpsell extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[role="list"]');
      this.moneyFormat = this.dataset.moneyFormat || 'Rs.{{amount_no_decimals_with_comma_separator}}';
      this.cartProductIds = new Set(
        (this.dataset.productIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      );
      this.abortController = new AbortController();
      this.loadRecommendations();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    async loadRecommendations() {
      const seedIds = [this.dataset.productId, ...this.cartProductIds].filter(Boolean);
      const uniqueSeeds = [...new Set(seedIds)];
      let products = [];

      for (const productId of uniqueSeeds) {
        products = await this.fetchRecommendations(productId);
        products = products.filter((product) => !this.cartProductIds.has(String(product.id)));
        if (products.length) break;
      }

      if (!products.length || !this.isConnected) {
        this.hidden = true;
        return;
      }

      this.render(products.slice(0, 8));
      this.hidden = false;
    }

    async fetchRecommendations(productId) {
      if (recCache.has(productId)) return recCache.get(productId);

      try {
        const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=12&intent=related`;
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) return [];
        const data = await response.json();
        const products = (data.products || [])
          .map((product) => this.normalizeProduct(product))
          .filter((product) => product && product.variantId && product.available);

        recCache.set(productId, products);
        return products;
      } catch (error) {
        if (error.name === 'AbortError') return [];
        console.warn('[cart-drawer-upsell]', error);
        return [];
      }
    }

    normalizeProduct(product) {
      const variants = product.variants || [];
      const variant = variants.find((item) => item.available) || null;
      if (!variant) return null;

      const image =
        variant.featured_image?.src ||
        product.featured_image ||
        product.images?.[0] ||
        '';

      return {
        id: product.id,
        title: product.title,
        url: product.url,
        image,
        variantId: variant.id,
        available: true,
        price: variant.price ?? product.price,
        compareAtPrice: variant.compare_at_price || product.compare_at_price,
      };
    }

    render(products) {
      if (!this.track) return;

      this.track.innerHTML = products
        .map((product) => {
          const image = product.image
            ? `<img src="${this.escape(this.imageUrl(product.image, 360))}" alt="${this.escape(product.title)}" width="180" height="180" loading="lazy">`
            : '<span class="cart-drawer-upsell__placeholder"></span>';
          const compare =
            product.compareAtPrice && Number(product.compareAtPrice) > Number(product.price)
              ? `<s class="cart-drawer-upsell__compare">${this.formatMoney(product.compareAtPrice)}</s>`
              : '';

          return `
            <article class="cart-drawer-upsell__card" role="listitem">
              <div class="cart-drawer-upsell__media-wrap">
                <a class="cart-drawer-upsell__media" href="${this.escape(product.url)}">${image}</a>
                <button
                  type="button"
                  class="cart-drawer-upsell__add"
                  data-variant-id="${product.variantId}"
                >
                  Add +
                </button>
              </div>
              <a class="cart-drawer-upsell__title" href="${this.escape(product.url)}">${this.escape(product.title)}</a>
              <div class="cart-drawer-upsell__row">
                <div class="cart-drawer-upsell__prices">
                  ${compare}
                  <span class="cart-drawer-upsell__price">${this.formatMoney(product.price)}</span>
                </div>
              </div>
            </article>
          `;
        })
        .join('');

      this.track.querySelectorAll('.cart-drawer-upsell__add').forEach((button) => {
        button.addEventListener('pointerdown', (event) => event.stopPropagation());
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.addToCart(event.currentTarget);
        });
      });
      this.bindTrackScroll();
    }

    bindTrackScroll() {
      const track = this.track;
      if (!track || track.dataset.scrollBound === 'true') return;
      track.dataset.scrollBound = 'true';

      track.addEventListener(
        'wheel',
        (event) => {
          if (track.scrollWidth <= track.clientWidth) return;
          const mostlyVertical = Math.abs(event.deltaY) > Math.abs(event.deltaX);
          if (!mostlyVertical && event.deltaX === 0) return;
          track.scrollLeft += mostlyVertical ? event.deltaY : event.deltaX;
          event.preventDefault();
        },
        { passive: false }
      );

      let pointerId = null;
      let startX = 0;
      let startLeft = 0;
      let dragging = false;
      let moved = false;

      const endDrag = (event) => {
        if (pointerId === null || event.pointerId !== pointerId) return;
        track.classList.remove('is-dragging');
        try {
          track.releasePointerCapture(pointerId);
        } catch (error) {}
        pointerId = null;
        dragging = false;
        if (moved) {
          track.dataset.didDrag = 'true';
          setTimeout(() => {
            delete track.dataset.didDrag;
          }, 50);
        }
      };

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button, a, input, label')) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startLeft = track.scrollLeft;
        dragging = true;
        moved = false;
      });

      track.addEventListener('pointermove', (event) => {
        if (!dragging || event.pointerId !== pointerId) return;
        const dx = event.clientX - startX;
        if (Math.abs(dx) < 6) return;
        moved = true;
        track.classList.add('is-dragging');
        if (track.hasPointerCapture?.(pointerId) !== true) {
          try {
            track.setPointerCapture(pointerId);
          } catch (error) {}
        }
        track.scrollLeft = startLeft - dx;
      });

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      track.addEventListener('click', (event) => {
        if (track.dataset.didDrag === 'true') {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
    }

    async addToCart(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;

      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      const cart = document.querySelector('cart-drawer');
      const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [
        'cart-drawer',
        'cart-icon-bubble',
      ];

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const json = await response.json();

        if (json.status) {
          button.textContent = 'Unavailable';
          return;
        }

        cart?.classList.remove('is-empty');
        cart?.querySelector('.drawer__inner')?.classList.remove('is-empty');

        if (cart?.renderContents && json.sections) {
          cart.renderContents(json);
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            productVariantId: variantId,
            cartData: json,
          });
        }
      } catch (error) {
        console.error('[cart-drawer-upsell] add', error);
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      }
    }

    imageUrl(src, width) {
      if (!src) return '';
      if (src.startsWith('//')) src = `https:${src}`;
      if (src.includes('&width=') || src.includes('?width=')) return src;
      return src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;
    }

    formatMoney(cents) {
      const value = Number(cents) / 100;
      if (Number.isNaN(value)) return '';
      const amount = value.toFixed(2);
      const amountNoDecimals = String(Math.round(value));
      const withCommas = this.addCommas(amount);
      const noDecimalsWithCommas = this.addCommas(amountNoDecimals);

      return this.moneyFormat
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimalsWithCommas)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimalsWithCommas)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, withCommas.replace(/,/g, "'"))
        .replace(/\{\{\s*amount\s*\}\}/g, withCommas);
    }

    addCommas(value) {
      const parts = String(value).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    escape(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
